import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { db } from '../../firebase'
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore'
import { PlusIcon, PencilIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline'

export default function AdminPanel() {
  const navigate = useNavigate()
  const [productos, setProductos] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingProduct, setEditingProduct] = useState(null)
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    precio: '',
    precioanterior: '',
    imagen: '',
    caracteristicas: '',
    ocultarprecio: false,
    categoria: 'producto'
  })

  useEffect(() => {
    const isAuth = localStorage.getItem('adminAuth')
    if (!isAuth) navigate('/admin-login')
    fetchProductos()
  }, [navigate])

  const fetchProductos = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'productos'))
      const productosData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      setProductos(productosData)
    } catch (error) {
      console.error('Error fetching productos:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleOpenModal = (product = null) => {
    if (product) {
      setEditingProduct(product)
      setFormData({
        nombre: product.nombre,
        descripcion: product.descripcion || '',
        precio: product.precio || '',
        precioanterior: product.precioanterior || '',
        imagen: product.imagen || '',
        caracteristicas: product.caracteristicas ? JSON.stringify(product.caracteristicas) : '',
        ocultarprecio: product.ocultarprecio || false,
        categoria: product.categoria || 'producto'
      })
    } else {
      setEditingProduct(null)
      setFormData({
        nombre: '',
        descripcion: '',
        precio: '',
        precioanterior: '',
        imagen: '',
        caracteristicas: '',
        ocultarprecio: false,
        categoria: 'producto'
      })
    }
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setEditingProduct(null)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      const payload = {
        nombre: formData.nombre,
        descripcion: formData.descripcion,
        precio: formData.precio ? parseFloat(formData.precio) : null,
        precioanterior: formData.precioanterior ? parseFloat(formData.precioanterior) : null,
        imagen: formData.imagen,
        caracteristicas: formData.caracteristicas ? JSON.parse(formData.caracteristicas) : null,
        ocultarprecio: formData.ocultarprecio,
        categoria: formData.categoria,
        updatedAt: new Date().toISOString()
      }

      if (editingProduct) {
        await updateDoc(doc(db, 'productos', editingProduct.id), payload)
      } else {
        await addDoc(collection(db, 'productos'), {
          ...payload,
          createdAt: new Date().toISOString()
        })
      }

      await fetchProductos()
      handleCloseModal()
    } catch (error) {
      console.error('Error saving product:', error)
      alert('Error: ' + error.message)
    }
  }

  const handleDelete = async (id) => {
    if (window.confirm('¿Eliminar este producto?')) {
      try {
        await deleteDoc(doc(db, 'productos', id))
        await fetchProductos()
      } catch (error) {
        console.error('Error deleting product:', error)
        alert('Error al eliminar')
      }
    }
  }

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-white">Gestión de Productos</h1>
        <button
          onClick={() => handleOpenModal()}
          className="flex items-center gap-2 px-6 py-3 bg-adrox-blue hover:bg-blue-600 text-white rounded-lg transition-colors font-medium"
        >
          <PlusIcon className="w-5 h-5" />
          Nuevo Producto
        </button>
      </div>

      {/* Lista de Productos */}
      <div className="bg-gray-900 rounded-lg border border-white/10 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-400">Cargando productos...</div>
        ) : productos.length === 0 ? (
          <div className="p-8 text-center text-gray-400">No hay productos creados</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-800 border-b border-white/10">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Nombre</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Descripción</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Precio</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Tipo</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-300">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {productos.map((product) => (
                  <tr key={product.id} className="hover:bg-gray-800/50 transition-colors">
                    <td className="px-6 py-4 text-sm text-white font-medium">{product.nombre}</td>
                    <td className="px-6 py-4 text-sm text-gray-300">{(product.descripcion || '').substring(0, 50)}...</td>
                    <td className="px-6 py-4 text-sm text-white">
                      S/. {product.precio || 0}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        product.categoria === 'servicio'
                          ? 'bg-green-500/20 text-green-300'
                          : 'bg-blue-500/20 text-blue-300'
                      }`}>
                        {product.categoria === 'servicio' ? 'Servicio' : 'Producto'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right space-x-2">
                      <button
                        onClick={() => handleOpenModal(product)}
                        className="inline-flex items-center gap-2 px-3 py-2 bg-blue-500/10 hover:bg-blue-500/20 text-blue-300 rounded transition-colors text-sm"
                      >
                        <PencilIcon className="w-4 h-4" />
                        Editar
                      </button>
                      <button
                        onClick={() => handleDelete(product.id)}
                        className="inline-flex items-center gap-2 px-3 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-300 rounded transition-colors text-sm"
                      >
                        <TrashIcon className="w-4 h-4" />
                        Eliminar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal Flotante */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-900 rounded-lg p-8 max-w-2xl w-full mx-4 border border-white/10 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white">
                {editingProduct ? 'Editar Producto' : 'Nuevo Producto'}
              </h2>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-2">Nombre</label>
                  <input
                    type="text"
                    required
                    value={formData.nombre}
                    onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-800 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-adrox-blue"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-2">Descripción</label>
                  <textarea
                    value={formData.descripcion}
                    onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-800 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-adrox-blue h-24"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Precio</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.precio}
                    onChange={(e) => setFormData({ ...formData, precio: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-800 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-adrox-blue"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Precio Anterior (Opcional)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.precioanterior}
                    onChange={(e) => setFormData({ ...formData, precioanterior: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-800 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-adrox-blue"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Tipo</label>
                  <select
                    value={formData.categoria}
                    onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-800 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-adrox-blue"
                  >
                    <option value="producto">Producto</option>
                    <option value="servicio">Servicio</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">URL Imagen</label>
                  <input
                    type="url"
                    value={formData.imagen}
                    onChange={(e) => setFormData({ ...formData, imagen: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-800 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-adrox-blue"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-2">Características (JSON)</label>
                  <textarea
                    value={formData.caracteristicas}
                    onChange={(e) => setFormData({ ...formData, caracteristicas: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-800 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-adrox-blue font-mono text-xs h-20"
                    placeholder='{"color": "rojo", "tamaño": "grande"}'
                  />
                </div>

                <div className="md:col-span-2 flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="ocultarprecio"
                    checked={formData.ocultarprecio}
                    onChange={(e) => setFormData({ ...formData, ocultarprecio: e.target.checked })}
                    className="w-4 h-4 text-adrox-blue bg-gray-800 border-white/10 rounded focus:ring-adrox-blue"
                  />
                  <label htmlFor="ocultarprecio" className="text-sm font-medium text-gray-300">
                    Ocultar precio (mostrar "Consultar precio")
                  </label>
                </div>
              </div>

              <div className="flex gap-3 pt-6 border-t border-white/10">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="flex-1 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-adrox-blue hover:bg-blue-600 text-white rounded-lg transition-colors font-medium"
                >
                  {editingProduct ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
